import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import DataTable from 'react-data-table-component';
import { getBanks, createBank, updateBank, deleteBank } from '../api/services';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

const DEFAULT = { name:'', shortCode:'', routingNumber:'', phone:'', address:'', website:'', notes:'' };

/* ── DataTable styles ──────────────────────────────────────── */
const dtStyles = {
  headRow:   { style: { backgroundColor:'var(--gray-4)', borderBottom:'1px solid var(--gray-3)', minHeight:'42px' } },
  headCells: { style: { fontSize:'11.5px', fontWeight:'700', color:'var(--gray-1)', textTransform:'uppercase', letterSpacing:'0.6px', paddingLeft:'16px', paddingRight:'12px' } },
  rows:      { style: { fontSize:'13.5px', color:'var(--dark)', borderBottom:'1px solid var(--gray-4)', minHeight:'56px', '&:hover':{ backgroundColor:'rgba(26,122,74,0.04)' } } },
  cells:     { style: { paddingLeft:'16px', paddingRight:'12px' } },
  pagination:{ style: { borderTop:'1px solid var(--gray-3)', fontSize:'13px', color:'var(--gray-1)', minHeight:'52px' }, pageButtonsStyle:{ borderRadius:'6px', color:'var(--dark)', fill:'var(--gray-1)', '&:hover:not(:disabled)':{ backgroundColor:'var(--gray-4)' } } },
  noData:    { style: { padding:'48px', color:'var(--gray-1)', fontSize:'14px' } },
  progress:  { style: { padding:'48px' } },
};

/* ── Add/Edit Modal ────────────────────────────────────────── */
function BankModal({ editId, form, setF, onClose, onSave, saving }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,25,35,0.6)', backdropFilter:'blur(4px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:20, width:520, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 60px rgba(0,0,0,0.28)' }}>
        <div style={{ background:'linear-gradient(135deg,#1a7a4a,#2da05e)', borderRadius:'20px 20px 0 0', padding:'22px 28px 18px', color:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:28 }}>{editId ? '✏️' : '🏦'}</span>
            <div>
              <div style={{ fontSize:19, fontWeight:800 }}>{editId ? 'Edit Bank' : 'Add Bank'}</div>
              <div style={{ fontSize:12.5, opacity:.85, marginTop:3 }}>Banks appear in the dropdown on the Cash a Check form.</div>
            </div>
          </div>
        </div>
        <div style={{ padding:'24px 28px', display:'flex', flexDirection:'column', gap:14 }}>
          {[
            { key:'name',          label:'Bank Name *',    placeholder:'e.g. Hancock Bank' },
            { key:'shortCode',     label:'Short Code',     placeholder:'e.g. HB' },
            { key:'routingNumber', label:'Routing Number', placeholder:'9-digit routing number', mono:true },
            { key:'phone',         label:'Phone',          placeholder:'(555) 000-0000' },
            { key:'address',       label:'Address',        placeholder:'Bank address' },
            { key:'website',       label:'Website',        placeholder:'https://…' },
          ].map(f => (
            <div key={f.key}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color: f.key==='name' ? '#1a7a4a' : '#64748b', marginBottom:5 }}>{f.label}</label>
              <input className="form-control" value={form[f.key]} placeholder={f.placeholder}
                style={f.mono ? {fontFamily:'monospace'} : {}}
                onChange={e => setF(f.key, e.target.value)} />
            </div>
          ))}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#64748b', marginBottom:5 }}>Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} placeholder="Internal notes…" onChange={e => setF('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ padding:'14px 28px 22px', display:'flex', gap:10, justifyContent:'flex-end', borderTop:'1px solid #f1f5f9' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || !form.name.trim()}>
            {saving ? '⏳ Saving…' : editId ? '💾 Update' : '🏦 Add Bank'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Banks Master Page Component.
 * Server-side paginated management table for bank records (CRUD operations and routing numbers).
 *
 * @component
 * @returns {JSX.Element} Banks master page view.
 */
export default function Banks() {
  const [banks,   setBanks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState('');
  const [draft,   setDraft]   = useState('');      // typing buffer
  const [page,    setPage]    = useState(1);
  const [limit,   setLimit]   = useState(20);
  const [modal,   setModal]   = useState(false);
  const [form,    setForm]    = useState(DEFAULT);
  const [editId,  setEditId]  = useState(null);
  const [saving,  setSaving]  = useState(false);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async (pg = page, lmt = limit, q = search) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: lmt };
      if (q) params.search = q;
      const r = await getBanks(params);
      setBanks(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load banks'); }
    finally  { setLoading(false); }
  }, [page, limit, search]);

  const lastFetchTimeRef   = useRef(0);
  const lastFetchParamsRef = useRef("");

  useEffect(() => {
    const paramsKey = JSON.stringify({ page, limit });
    const now = Date.now();
    if (paramsKey === lastFetchParamsRef.current && now - lastFetchTimeRef.current < 400) return;
    lastFetchParamsRef.current = paramsKey;
    lastFetchTimeRef.current = now;
    load(page, limit, search);
  }, [page, limit]); // eslint-disable-line

  const handleSearch = () => { setSearch(draft); setPage(1); load(1, limit, draft); };
  const handleClear  = () => { setDraft(''); setSearch(''); setPage(1); load(1, limit, ''); };

  const openAdd  = () => { setEditId(null); setForm(DEFAULT); setModal(true); };
  const openEdit = b => { setEditId(b._id); setForm({ name:b.name, shortCode:b.shortCode||'', routingNumber:b.routingNumber||'', phone:b.phone||'', address:b.address||'', website:b.website||'', notes:b.notes||'' }); setModal(true); };
  const closeModal = () => { setModal(false); setEditId(null); setForm(DEFAULT); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bank name is required'); return; }
    setSaving(true);
    try {
      editId ? await updateBank(editId, form) : await createBank(form);
      toast.success(editId ? 'Bank updated!' : `"${form.name}" added.`);
      closeModal(); load(page, limit, search);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    const r = await Swal.fire({ title:'Remove Bank?', html:`Remove <b>${name}</b> from the bank list?`, icon:'warning', showCancelButton:true, confirmButtonText:'Yes, Remove', confirmButtonColor:'#e03c3c', cancelButtonColor:'#6b7280' });
    if (!r.isConfirmed) return;
    try { await deleteBank(id); toast.success(`"${name}" removed.`); load(page, limit, search); }
    catch { toast.error('Delete failed'); }
  };

  /* ── Columns ──────────────────────────────────────── */
  const columns = [
    {
      name: '#', minWidth: '50px', maxWidth: '60px',
      cell: (r, i) => <span style={{ fontSize:12, color:'#94a3b8', fontFamily:'monospace' }}>{(page-1)*limit + i + 1}</span>,
    },
    {
      name: 'BANK NAME', selector: r => r.name, sortable: true, minWidth: '220px',
      cell: r => (
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:38, height:38, borderRadius:9, background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>🏦</div>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>{r.name}</div>
            {r.address && <div style={{ fontSize:11, color:'#64748b' }}>{r.address}</div>}
          </div>
        </div>
      ),
    },
    {
      name: 'SHORT CODE', selector: r => r.shortCode, sortable: true, minWidth: '110px',
      cell: r => r.shortCode
        ? <span style={{ background:'#f0fdf4', color:'#1a7a4a', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700 }}>{r.shortCode}</span>
        : <span style={{ opacity:.35 }}>—</span>,
    },
    {
      name: 'ROUTING #', selector: r => r.routingNumber, sortable: true, minWidth: '130px',
      cell: r => <span style={{ fontSize:13, fontFamily:'monospace', color:'#475569' }}>{r.routingNumber || <span style={{opacity:.35}}>—</span>}</span>,
    },
    {
      name: 'PHONE', selector: r => r.phone, minWidth: '130px',
      cell: r => <span style={{ fontSize:13, color:'#475569' }}>{r.phone || <span style={{opacity:.35}}>—</span>}</span>,
    },
    {
      name: 'ADDED BY', selector: r => r.createdBy?.name, minWidth: '110px',
      cell: r => <span style={{ fontSize:12, color:'#64748b' }}>{r.createdBy?.name || '—'}</span>,
    },
    {
      name: 'ACTIONS', minWidth: '100px', ignoreRowClick: true,
      cell: r => (
        <div style={{ display:'flex', gap:6 }}>
          <button className="btn btn-sm btn-outline" onClick={() => openEdit(r)}>✏️</button>
          <button className="btn btn-sm" style={{ background:'#fef2f2', color:'#e03c3c', border:'1px solid #fca5a5', fontWeight:700 }} onClick={() => handleDelete(r._id, r.name)}>🗑️</button>
        </div>
      ),
    },
  ];

  return (
    <Layout title="Bank Master">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:'#0f1923' }}>🏦 Bank Master</h2>
          <p style={{ fontSize:13, color:'#64748b', marginTop:3 }}>
            {loading ? 'Loading…' : `${total} bank${total!==1?'s':''} · used in Cash a Check dropdown`}
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => load(page, limit, search)} className="btn btn-outline btn-sm">🔄 Refresh</button>
          <button onClick={openAdd} className="btn btn-primary btn-sm">🏦 + Add Bank</button>
        </div>
      </div>

      {/* Search — Search button driven */}
      <div className="card mb-4" style={{ padding:'12px 16px' }}>
        <div style={{ display:'flex', gap:8 }}>
          <input className="form-control" placeholder="Search bank name…" value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key==='Enter' && handleSearch()}
            style={{ flex:1 }} />
          {draft && <button className="btn btn-outline btn-sm" onClick={handleClear}>✕</button>}
          <button className="btn btn-primary btn-sm" onClick={handleSearch} style={{ fontWeight:700 }}>🔍 Search</button>
        </div>
      </div>

      {modal && <BankModal editId={editId} form={form} setF={setF} onClose={closeModal} onSave={handleSave} saving={saving} />}

      <div className="card">
        <DataTable
          columns={columns}
          data={banks}
          progressPending={loading}
          progressComponent={<div className="table-loading-cell"><div className="spinner spinner-dark spinner-centered"/></div>}
          noDataComponent={
            <div className="table-empty-cell">
              <div className="empty-icon">🏦</div>
              <p>{search ? `No banks match "${search}"` : 'No banks yet. Add your first bank.'}</p>
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationDefaultPage={page}
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangePage={p => setPage(p)}
          onChangeRowsPerPage={(newLimit, p) => { setLimit(newLimit); setPage(p); }}
          highlightOnHover
          customStyles={dtStyles}
        />
      </div>
    </Layout>
  );
}
